import { estBloc, montantLigne } from './blocs-lignes';
import { PREFIXE_DEDUCTION } from './vat';

const nombre = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const arrondi = (n) => Math.round(n * 100) / 100;

/**
 * La marge d'une offre ou d'une facture du magasin : ce que la marchandise
 * se vend (remises de ligne et remise globale déduites), moins ce qu'elle a
 * coûté au prix d'achat du stock.
 *
 * <p>Seules les lignes dont le prix d'achat est connu entrent dans la marge :
 * compter la vente d'un article hors stock sans son coût gonflerait le
 * bénéfice. Elles sont comptées à part, pour le signaler. Un bloc compte
 * quand tous ses articles ont leur prix d'achat.
 *
 * @param {object[]} lignes lignes de la pièce
 * @param {object[]} stock articles du stock (ref, pa, tva)
 * @param {object} [doc] la pièce
 * @param {number} [doc.remisePct] remise globale, en %
 * @param {number} [doc.tvaPct] taux du document, pour une ligne sans taux propre
 * @returns {{venteHt: number, venteTtc: number, achatHt: number, achatTtc: number,
 *   sansPrixAchat: number, venteSansPrixAchatHt: number}} la marge
 */
export function margePiece(lignes, stock, { remisePct = 0, tvaPct = 0 } = {}) {
  const parRef = new Map((stock || []).map((a) => [String(a.ref), a]));
  const global = 1 - nombre(remisePct) / 100;
  const achatDe = (l) => {
    const a = l.ref != null ? parRef.get(String(l.ref)) : null;
    const pa = nombre(a ? a.pa : l.pa);
    const q = nombre(l.q);
    if (!pa || !q) return null;
    const taux = nombre(a?.tva ?? l.tva ?? tvaPct);
    return { ht: q * pa, ttc: q * pa * (1 + taux / 100) };
  };
  const m = {
    venteHt: 0,
    venteTtc: 0,
    achatHt: 0,
    achatTtc: 0,
    sansPrixAchat: 0,
    venteSansPrixAchatHt: 0,
  };
  for (const l of lignes || []) {
    // La déduction d'un acompte déjà facturé n'est pas une vente : elle
    // comptait comme une ligne « sans prix d'achat » sur la facture finale.
    if (String(l.cle || '').startsWith(PREFIXE_DEDUCTION)) continue;
    const vente = montantLigne(l) * global;
    if (!vente) continue;
    const articles = estBloc(l) && Array.isArray(l.bloc) ? l.bloc : [l];
    const achats = estBloc(l) && !Array.isArray(l.bloc) ? [null] : articles.map(achatDe);
    if (achats.some((x) => !x)) {
      m.sansPrixAchat += 1;
      m.venteSansPrixAchatHt += vente;
      continue;
    }
    const taux = l.taxe === false ? 0 : nombre(l.tva ?? tvaPct);
    m.venteHt += vente;
    m.venteTtc += vente * (1 + taux / 100);
    for (const a of achats) {
      m.achatHt += a.ht;
      m.achatTtc += a.ttc;
    }
  }
  return Object.fromEntries(
    Object.entries(m).map(([k, v]) => [k, k === 'sansPrixAchat' ? v : arrondi(v)]),
  );
}
